import React, { type ReactElement, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import ConfirmModal, { ConfirmModalActionPair } from 'atom-components/confirm-modal';
import {
  type MarkItemFormValue,
  type MarkResponseFormValues,
  unrefinedMarkResponseFormSchema
} from 'feature-components/mark-response/mark-response-form/mark-response-form.schema';
import { pluralise } from 'helpers/string.helpers';
import useAllocationHandler from 'hooks/marking/use-allocation-handler';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';

/**
 * Returns the mark items that will be added or have been changed by the current user.
 */
export function newAndChangedMarkItems(
  savedMarkItems: MarkItemFormValue[],
  markItems: MarkItemFormValue[]
): MarkItemFormValue[] {
  const changedMarkItems = markItems
    .filter((mi) => mi.id !== undefined)
    .filter((mi) =>
      savedMarkItems.find((smi) => smi.id === mi.id && smi.submittedValue !== mi.submittedValue)
    );
  const newMarkItems = markItems.filter((mi) => mi.id === undefined && mi.submittedValue !== '');
  return changedMarkItems.concat(newMarkItems);
}

/**
 * If a user who can administrate the mark has not been set as the active marker and they have
 * rescored some mark items, prevent saving until they have confirmed that they should be set as the
 * active marker. This check also applies to independent markers who are rescoring another marker's
 * mark to confirm the new marker.
 *
 * Also prevent saving if there is no active marker and the current user can administrate the mark -
 * this provides the marking admin the opportunity to set themselves as the active marker of a new
 * mark or of a saved mark where there is somehow no active marker set. Skip this check if the user
 * can mark independently but cannot administrate the mark so that they can be silently set as the
 * active marker just before creating the mark without additional noise.
 *
 * Markers who cannot administrate or mark independently (regular markers who are explicitly
 * allocated) should never get this far into the form as they cannot view other markers marks.
 * However, explicitly allocated markers should not be able to reassign marks, so prevent the modal
 * from appearing in case they somehow got this far.
 */
export function shouldAskToChangeActiveMarker(
  canAdministrateMark: boolean,
  canMarkIndependently: boolean,
  defaultValues: MarkResponseFormValues,
  formValues: MarkResponseFormValues
): boolean {
  // Explicitly allocated markers should not be able to reassign marks:
  if (!canAdministrateMark && !canMarkIndependently) return false;

  if (!formValues.activeMarkerId) {
    // Independent markers don't need to be asked when creating a mark as they should be silently
    // allocated/assigned to the mark:
    if (!canAdministrateMark && canMarkIndependently) {
      return false;
    }
    // Users who can administrate should be asked before creating a new mark mark as themselves as
    // the active marker, as it could be unintentional:
    return true;
  }

  // All user roles except regular markers should be asked if the active and current marker don't
  // match and an item has been rescored:
  if (
    formValues.currentUserMarkerId !== formValues.activeMarkerId &&
    newAndChangedMarkItems(defaultValues.markItems, formValues.markItems).length > 0
  ) {
    return true;
  }

  // If a mark item hasn't been rescored and there is an active marker, there's no need to ask if
  // the active marker should be changed (other fields such as the comment can be changed without
  // the active marker changing):
  return false;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  responseId: number;
  currentOrganisationUser: MeCustomerUser;
};

export default function ConfirmMarkerChangeModal({
  isOpen,
  onClose,
  onConfirm,
  responseId,
  currentOrganisationUser
}: Props): ReactElement {
  const [isLoading, setIsLoading] = useState(false);

  const { watch, formState, setValue } = useFormContext();

  const activeMarkerId = watch(unrefinedMarkResponseFormSchema.keyof().Enum.activeMarkerId);
  const markItems = watch(unrefinedMarkResponseFormSchema.keyof().Enum.markItems);
  const newChangedMarkItems = newAndChangedMarkItems(formState.defaultValues?.markItems, markItems);

  const { assignCurrentUserToMark, isAllocating } = useAllocationHandler(
    responseId,
    currentOrganisationUser.id,
    (markerId: number | undefined) => {
      setValue(unrefinedMarkResponseFormSchema.keyof().Enum.currentUserMarkerId, markerId);
      setValue(unrefinedMarkResponseFormSchema.keyof().Enum.activeMarkerId, markerId);

      // Ensure that a re-render occurs after calling setValue and before called onConfirm,
      // otherwise the modal can appear twice:
      setIsLoading(false);
      onConfirm();
    }
  );

  async function handleConfirmClick(): Promise<void> {
    // Ensure that the user cannot click the button twice, which can trigger API requests multiple
    // times.
    if (isLoading) return;

    setIsLoading(true);
    await assignCurrentUserToMark();
  }

  const isConfirming = isLoading || isAllocating;

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Confirm Marker Change"
      handleClickCancel={onClose}
      handleClickOK={(): Promise<void> => handleConfirmClick()}
      actionPair={ConfirmModalActionPair.Custom}
      buttonTitleOK="Change Active Marker"
      buttonTitleCancel="Cancel"
      disableOKButton={isConfirming}
    >
      <p>
        {activeMarkerId
          ? `You have made changes to ${newChangedMarkItems.length} ${pluralise('item', newChangedMarkItems.length)}.`
          : `There is currently no active marker.`}{' '}
        To save these changes you must be assigned as the active marker.
      </p>
    </ConfirmModal>
  );
}
